/**
 * DOL FOIA Dataset Browser
 *
 * Browse, download, search, and import DOL Form 5500 FOIA bulk datasets.
 * Connects to the backend FOIA service endpoints for:
 * - Listing available dataset years with cache status
 * - Triggering bulk dataset downloads
 * - Searching/filtering downloaded data with pre-import criteria
 * - Importing filtered results through the ETL pipeline
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Download,
  Search,
  Loader2,
  CheckCircle,
  AlertCircle,
  Database,
  HardDrive,
  Filter,
  Play,
  RefreshCw,
  Shield,
  Building2,
  MapPin,
  FileText,
  Info,
  ChevronRight,
  ExternalLink,
  X,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Slider } from '@/components/ui/slider';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';
import {
  getFOIADatasets,
  downloadFOIADatasets,
  searchFOIAData,
  importFOIAData,
  type FOIADatasetYear,
  type FOIASearchFilters,
  type FOIASearchResult,
  type FOIASearchResponse,
  type FOIAImportResponse,
} from '@/services/api/integrations';

const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY', 'DC',
];

type BrowserStep = 'datasets' | 'search' | 'results' | 'import';

interface DOLDatasetBrowserProps {
  onImportComplete?: (result: FOIAImportResponse) => void;
  onCancel?: () => void;
  targetTable?: 'employers' | 'lb_company_matches';
}

export const DOLDatasetBrowser: React.FC<DOLDatasetBrowserProps> = ({
  onImportComplete,
  onCancel,
  targetTable: initialTargetTable = 'lb_company_matches',
}) => {
  // Step navigation
  const [activeStep, setActiveStep] = useState<BrowserStep>('datasets');

  // Dataset state
  const [datasets, setDatasets] = useState<FOIADatasetYear[]>([]);
  const [loadingDatasets, setLoadingDatasets] = useState(false);
  const [selectedYears, setSelectedYears] = useState<number[]>([2023, 2022]);
  const [downloading, setDownloading] = useState<Record<number, boolean>>({});

  // Search state
  const [filters, setFilters] = useState<FOIASearchFilters>({
    years: [2023, 2022],
    states: null,
    min_participants: null,
    max_participants: null,
    self_insured_only: true,
    min_self_insured_confidence: 0.4,
    company_name_search: null,
    ein_search: null,
    exclude_small_plans: false,
    limit: 500,
  });
  const [filterStates, setFilterStates] = useState<string[]>([]);
  const [searching, setSearching] = useState(false);
  const [searchResults, setSearchResults] = useState<FOIASearchResponse | null>(null);

  // Import state
  const [targetTable, setTargetTable] = useState<'employers' | 'lb_company_matches'>(initialTargetTable);
  const [autoApproveThreshold, setAutoApproveThreshold] = useState(0.8);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<FOIAImportResponse | null>(null);

  // Error state
  const [error, setError] = useState<string | null>(null);

  // Load available datasets
  const loadDatasets = useCallback(async () => {
    setLoadingDatasets(true);
    setError(null);
    try {
      const response = await getFOIADatasets();
      setDatasets(response.years);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load datasets');
    } finally {
      setLoadingDatasets(false);
    }
  }, []);

  useEffect(() => {
    loadDatasets();
  }, [loadDatasets]);

  // Download datasets
  const handleDownload = async (years: number[]) => {
    setError(null);
    const newDownloading: Record<number, boolean> = {};
    years.forEach(y => newDownloading[y] = true);
    setDownloading(prev => ({ ...prev, ...newDownloading }));

    try {
      const response = await downloadFOIADatasets({ years, force: false });
      const failed = response.results.filter(r => r.status === 'failed');
      if (failed.length > 0) {
        setError(`Download failed for years: ${failed.map(f => `${f.year} (${f.message})`).join(', ')}`);
      }
      await loadDatasets();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Download failed');
    } finally {
      const cleared: Record<number, boolean> = {};
      years.forEach(y => cleared[y] = false);
      setDownloading(prev => ({ ...prev, ...cleared }));
    }
  };

  // Toggle year selection
  const toggleYear = (year: number) => {
    setSelectedYears(prev =>
      prev.includes(year) ? prev.filter(y => y !== year) : [...prev, year]
    );
  };

  // Search datasets
  const handleSearch = async () => {
    setError(null);
    setSearching(true);

    const searchFilters: FOIASearchFilters = {
      ...filters,
      years: selectedYears,
      states: filterStates.length > 0 ? filterStates : null,
    };

    try {
      const response = await searchFOIAData(searchFilters);
      setSearchResults(response);
      setActiveStep('results');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Search failed');
    } finally {
      setSearching(false);
    }
  };

  // Import results
  const handleImport = async () => {
    if (!searchResults) return;

    setError(null);
    setImporting(true);

    try {
      const result = await importFOIAData({
        years: selectedYears,
        filters: {
          ...filters,
          years: selectedYears,
          states: filterStates.length > 0 ? filterStates : null,
        },
        target_table: targetTable,
        auto_approve_threshold: autoApproveThreshold,
      });
      setImportResult(result);
      setActiveStep('import');
      onImportComplete?.(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  // Format file size
  const formatSize = (bytes: number | null) => {
    if (!bytes) return '-';
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Render dataset list
  const renderDatasetStep = () => (
    <div className="space-y-6">
      <div className="text-center">
        <h3 className="text-lg font-semibold mb-2">DOL Form 5500 FOIA Datasets</h3>
        <p className="text-muted-foreground">
          Select and download DOL bulk datasets, then search and filter before importing.
        </p>
      </div>

      <Alert>
        <Info className="h-4 w-4" />
        <AlertTitle>Bulk FOIA Data</AlertTitle>
        <AlertDescription>
          Data is sourced from{' '}
          <a
            href="https://www.dol.gov/agencies/ebsa/about-ebsa/our-activities/public-disclosure/foia/form-5500-datasets"
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary underline inline-flex items-center gap-1"
          >
            DOL FOIA datasets <ExternalLink className="h-3 w-3" />
          </a>
          . Large files (50-200MB) are cached locally after first download.
        </AlertDescription>
      </Alert>

      {loadingDatasets ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : (
        <div className="space-y-3">
          {datasets.map((dataset) => (
            <Card
              key={dataset.year}
              className={cn(
                'cursor-pointer transition-all',
                selectedYears.includes(dataset.year)
                  ? 'ring-2 ring-primary bg-primary/5'
                  : 'hover:bg-muted/50'
              )}
              onClick={() => toggleYear(dataset.year)}
            >
              <CardContent className="py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className={cn(
                      'w-10 h-10 rounded-lg flex items-center justify-center',
                      selectedYears.includes(dataset.year) ? 'bg-primary/20' : 'bg-muted'
                    )}>
                      <Database className={cn(
                        'h-5 w-5',
                        selectedYears.includes(dataset.year) ? 'text-primary' : 'text-muted-foreground'
                      )} />
                    </div>
                    <div>
                      <p className="font-semibold text-lg">{dataset.year}</p>
                      <div className="flex items-center gap-3 text-sm text-muted-foreground">
                        {dataset.cached ? (
                          <>
                            <span className="flex items-center gap-1">
                              <HardDrive className="h-3 w-3" />
                              {formatSize(dataset.file_size_bytes)}
                            </span>
                            {dataset.record_count && (
                              <span>{dataset.record_count.toLocaleString()} records</span>
                            )}
                          </>
                        ) : (
                          <span>Not downloaded</span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    {dataset.cached ? (
                      <Badge variant="outline" className="text-green-600 border-green-600">
                        <CheckCircle className="h-3 w-3 mr-1" />
                        Cached
                      </Badge>
                    ) : downloading[dataset.year] ? (
                      <Badge variant="outline" className="text-blue-600 border-blue-600">
                        <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                        Downloading
                      </Badge>
                    ) : (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDownload([dataset.year]);
                        }}
                      >
                        <Download className="h-3 w-3 mr-1" />
                        Download
                      </Button>
                    )}

                    <div className={cn(
                      'w-5 h-5 rounded border-2 flex items-center justify-center',
                      selectedYears.includes(dataset.year)
                        ? 'bg-primary border-primary'
                        : 'border-muted-foreground/30'
                    )}>
                      {selectedYears.includes(dataset.year) && (
                        <CheckCircle className="h-3 w-3 text-primary-foreground" />
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}

          {datasets.length === 0 && (
            <div className="text-center py-12 text-muted-foreground">
              <Database className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No dataset information available. Check your backend connection.</p>
            </div>
          )}
        </div>
      )}

      <div className="flex justify-between">
        {onCancel && (
          <Button variant="outline" onClick={onCancel}>
            Cancel
          </Button>
        )}
        <div className="ml-auto flex items-center gap-3">
          {selectedYears.some(y => !datasets.find(d => d.year === y)?.cached) && (
            <Button
              variant="outline"
              onClick={() => {
                const uncached = selectedYears.filter(y => !datasets.find(d => d.year === y)?.cached);
                if (uncached.length > 0) handleDownload(uncached);
              }}
              disabled={Object.values(downloading).some(Boolean)}
            >
              {Object.values(downloading).some(Boolean) ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Download className="h-4 w-4 mr-2" />
              )}
              Download Selected
            </Button>
          )}
          <Button
            onClick={() => {
              setFilters(prev => ({ ...prev, years: selectedYears }));
              setActiveStep('search');
            }}
            disabled={selectedYears.length === 0 || selectedYears.some(y => !datasets.find(d => d.year === y)?.cached)}
          >
            Configure Search
            <ChevronRight className="h-4 w-4 ml-2" />
          </Button>
        </div>
      </div>
    </div>
  );

  // Render search/filter configuration
  const renderSearchStep = () => (
    <div className="space-y-6">
      <div className="text-center">
        <h3 className="text-lg font-semibold mb-2">Search & Filter</h3>
        <p className="text-muted-foreground">
          Configure filters to search across {selectedYears.join(', ')} datasets
        </p>
      </div>

      <div className="grid gap-6">
        {/* Company Name Search */}
        <div className="space-y-2">
          <Label>Company Name Search</Label>
          <Input
            placeholder="Search by company name..."
            value={filters.company_name_search || ''}
            onChange={(e) => setFilters(prev => ({
              ...prev,
              company_name_search: e.target.value || null
            }))}
          />
        </div>

        {/* EIN Search */}
        <div className="space-y-2">
          <Label>EIN Search</Label>
          <Input
            placeholder="EIN prefix or exact match (e.g., 12-345)"
            value={filters.ein_search || ''}
            onChange={(e) => setFilters(prev => ({
              ...prev,
              ein_search: e.target.value || null
            }))}
          />
        </div>

        {/* Self-insured filter */}
        <div className="flex items-center justify-between">
          <div>
            <Label>Self-Insured Only</Label>
            <p className="text-sm text-muted-foreground">
              Only return employers detected as self-insured
            </p>
          </div>
          <Switch
            checked={filters.self_insured_only || false}
            onCheckedChange={(checked) =>
              setFilters(prev => ({ ...prev, self_insured_only: checked }))
            }
          />
        </div>

        {/* Min SI Confidence */}
        {filters.self_insured_only && (
          <div className="space-y-2">
            <Label>
              Min Self-Insured Confidence: {((filters.min_self_insured_confidence || 0.4) * 100).toFixed(0)}%
            </Label>
            <Slider
              value={[filters.min_self_insured_confidence || 0.4]}
              onValueChange={([value]) =>
                setFilters(prev => ({ ...prev, min_self_insured_confidence: value }))
              }
              min={0.1}
              max={1}
              step={0.05}
            />
          </div>
        )}

        {/* Participant count */}
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Min Participants</Label>
            <Input
              type="number"
              placeholder="No minimum"
              value={filters.min_participants || ''}
              onChange={(e) => setFilters(prev => ({
                ...prev,
                min_participants: e.target.value ? parseInt(e.target.value) : null
              }))}
            />
          </div>
          <div className="space-y-2">
            <Label>Max Participants</Label>
            <Input
              type="number"
              placeholder="No maximum"
              value={filters.max_participants || ''}
              onChange={(e) => setFilters(prev => ({
                ...prev,
                max_participants: e.target.value ? parseInt(e.target.value) : null
              }))}
            />
          </div>
        </div>

        {/* Exclude small plans */}
        <div className="flex items-center justify-between">
          <div>
            <Label>Exclude Small Plans</Label>
            <p className="text-sm text-muted-foreground">
              Exclude plans with fewer than 100 participants
            </p>
          </div>
          <Switch
            checked={filters.exclude_small_plans || false}
            onCheckedChange={(checked) =>
              setFilters(prev => ({ ...prev, exclude_small_plans: checked }))
            }
          />
        </div>

        {/* State filter */}
        <div className="space-y-2">
          <Label>Filter by States</Label>
          <div className="flex flex-wrap gap-1 mb-2">
            {filterStates.map((state) => (
              <Badge
                key={state}
                variant="secondary"
                className="cursor-pointer"
                onClick={() => setFilterStates(prev => prev.filter(s => s !== state))}
              >
                {state} <X className="h-3 w-3 ml-1" />
              </Badge>
            ))}
          </div>
          <Select
            value=""
            onValueChange={(value) => {
              if (value && !filterStates.includes(value)) {
                setFilterStates(prev => [...prev, value]);
              }
            }}
          >
            <SelectTrigger>
              <SelectValue placeholder="Add state filter..." />
            </SelectTrigger>
            <SelectContent>
              {US_STATES.filter(s => !filterStates.includes(s)).map((state) => (
                <SelectItem key={state} value={state}>{state}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Result limit */}
        <div className="space-y-2">
          <Label>Max Results: {filters.limit}</Label>
          <Slider
            value={[filters.limit || 500]}
            onValueChange={([value]) =>
              setFilters(prev => ({ ...prev, limit: value }))
            }
            min={50}
            max={5000}
            step={50}
          />
        </div>
      </div>

      <div className="flex justify-between">
        <Button variant="outline" onClick={() => setActiveStep('datasets')}>
          Back
        </Button>
        <Button onClick={handleSearch} disabled={searching}>
          {searching ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Searching...
            </>
          ) : (
            <>
              <Search className="h-4 w-4 mr-2" />
              Search Datasets
            </>
          )}
        </Button>
      </div>
    </div>
  );

  // Render search results
  const renderResultsStep = () => {
    if (!searchResults) return null;

    const siCount = searchResults.results.filter(r => r.is_self_insured).length;

    return (
      <div className="space-y-6">
        <div className="text-center">
          <h3 className="text-lg font-semibold mb-2">Search Results</h3>
          <p className="text-muted-foreground">
            Found {searchResults.total_results.toLocaleString()} matching records
          </p>
        </div>

        {/* Summary stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-4 text-center">
              <p className="text-2xl font-bold">{searchResults.total_results.toLocaleString()}</p>
              <p className="text-sm text-muted-foreground">Total Results</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 text-center">
              <p className="text-2xl font-bold text-green-600">{siCount.toLocaleString()}</p>
              <p className="text-sm text-muted-foreground">Self-Insured</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 text-center">
              <p className="text-2xl font-bold text-blue-600">
                {searchResults.years_searched.join(', ')}
              </p>
              <p className="text-sm text-muted-foreground">Years Searched</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 text-center">
              <p className="text-2xl font-bold">
                {Object.keys(searchResults.filters_applied).length}
              </p>
              <p className="text-sm text-muted-foreground">Filters Applied</p>
            </CardContent>
          </Card>
        </div>

        {/* Import Configuration */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Import Configuration</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Import Destination</Label>
              <Select
                value={targetTable}
                onValueChange={(value: 'employers' | 'lb_company_matches') => setTargetTable(value)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="employers">
                    <div className="flex items-center gap-2">
                      <Building2 className="h-4 w-4" />
                      <span>Employers (Direct import)</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="lb_company_matches">
                    <div className="flex items-center gap-2">
                      <FileText className="h-4 w-4" />
                      <span>Lead Discovery (Sales prospecting)</span>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Auto-Approve Threshold: {(autoApproveThreshold * 100).toFixed(0)}%</Label>
              <Slider
                value={[autoApproveThreshold]}
                onValueChange={([value]) => setAutoApproveThreshold(value)}
                min={0.5}
                max={1}
                step={0.05}
              />
              <p className="text-sm text-muted-foreground">
                Records with confidence above this threshold are auto-approved
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Results table */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              Preview (First {Math.min(searchResults.results.length, 25)} of {searchResults.total_results.toLocaleString()})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Company Name</TableHead>
                    <TableHead>EIN</TableHead>
                    <TableHead>Location</TableHead>
                    <TableHead className="text-right">Participants</TableHead>
                    <TableHead>Self-Insured</TableHead>
                    <TableHead className="text-right">SI Confidence</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {searchResults.results.slice(0, 25).map((result, idx) => (
                    <TableRow key={idx}>
                      <TableCell className="font-medium max-w-[200px] truncate">
                        {result.company_name || '-'}
                      </TableCell>
                      <TableCell className="font-mono text-sm">{result.ein || '-'}</TableCell>
                      <TableCell>
                        {result.city && result.state
                          ? `${result.city}, ${result.state}`
                          : result.state || '-'}
                      </TableCell>
                      <TableCell className="text-right">
                        {result.participant_count?.toLocaleString() || '-'}
                      </TableCell>
                      <TableCell>
                        {result.is_self_insured ? (
                          <Badge variant="default" className="bg-green-600">
                            <Shield className="h-3 w-3 mr-1" />
                            Yes
                          </Badge>
                        ) : (
                          <Badge variant="outline">No</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <Badge
                          variant="outline"
                          className={cn(
                            result.self_insured_confidence >= 0.7
                              ? 'text-green-600 border-green-600'
                              : result.self_insured_confidence >= 0.4
                              ? 'text-amber-600 border-amber-600'
                              : 'text-gray-500 border-gray-400'
                          )}
                        >
                          {(result.self_insured_confidence * 100).toFixed(0)}%
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-between">
          <Button variant="outline" onClick={() => setActiveStep('search')}>
            Back to Filters
          </Button>
          <Button onClick={handleImport} disabled={importing || searchResults.total_results === 0}>
            {importing ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Importing...
              </>
            ) : (
              <>
                <Play className="h-4 w-4 mr-2" />
                Import {searchResults.total_results.toLocaleString()} Records
              </>
            )}
          </Button>
        </div>
      </div>
    );
  };

  // Render import results
  const renderImportStep = () => {
    if (!importResult) return null;

    const isSuccess = importResult.status === 'completed';
    const hasErrors = importResult.status === 'completed_with_errors';

    return (
      <div className="space-y-6">
        <div className="text-center">
          {isSuccess ? (
            <CheckCircle className="h-16 w-16 text-green-600 mx-auto mb-4" />
          ) : hasErrors ? (
            <AlertCircle className="h-16 w-16 text-amber-600 mx-auto mb-4" />
          ) : (
            <AlertCircle className="h-16 w-16 text-red-600 mx-auto mb-4" />
          )}
          <h3 className="text-lg font-semibold mb-2">
            {isSuccess ? 'Import Complete!' : hasErrors ? 'Import Completed with Warnings' : 'Import Failed'}
          </h3>
          <p className="text-muted-foreground">
            {importResult.records_created.toLocaleString()} records imported successfully
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-4 text-center">
              <p className="text-2xl font-bold text-green-600">
                {importResult.records_created.toLocaleString()}
              </p>
              <p className="text-sm text-muted-foreground">Created</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 text-center">
              <p className="text-2xl font-bold text-blue-600">
                {importResult.records_updated.toLocaleString()}
              </p>
              <p className="text-sm text-muted-foreground">Updated</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 text-center">
              <p className="text-2xl font-bold text-amber-600">
                {importResult.records_skipped.toLocaleString()}
              </p>
              <p className="text-sm text-muted-foreground">Skipped</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 text-center">
              <p className="text-2xl font-bold text-red-600">
                {importResult.records_failed.toLocaleString()}
              </p>
              <p className="text-sm text-muted-foreground">Failed</p>
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Import Details</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Job ID:</span>
                <span className="font-mono">{importResult.job_id}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Status:</span>
                <Badge variant={isSuccess ? 'default' : hasErrors ? 'secondary' : 'destructive'}>
                  {importResult.status}
                </Badge>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Self-insured detected:</span>
                <span className="font-medium">{importResult.self_insured_detected.toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Duration:</span>
                <span className="font-medium">
                  {importResult.duration_seconds ? `${importResult.duration_seconds}s` : '-'}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-between">
          <Button variant="outline" onClick={() => {
            setActiveStep('datasets');
            setSearchResults(null);
            setImportResult(null);
          }}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Search Again
          </Button>
          {onCancel && (
            <Button onClick={onCancel}>Done</Button>
          )}
        </div>
      </div>
    );
  };

  // Step indicator
  const steps = [
    { id: 'datasets' as const, label: 'Select Data', icon: Database },
    { id: 'search' as const, label: 'Filter', icon: Filter },
    { id: 'results' as const, label: 'Preview', icon: FileText },
    { id: 'import' as const, label: 'Import', icon: CheckCircle },
  ];

  return (
    <div className="max-w-4xl mx-auto">
      {/* Step indicator */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          {steps.map((step, idx) => {
            const Icon = step.icon;
            const stepOrder = steps.findIndex(s => s.id === activeStep);
            const isActive = step.id === activeStep;
            const isCompleted = idx < stepOrder;

            return (
              <React.Fragment key={step.id}>
                <div className="flex flex-col items-center">
                  <div
                    className={cn(
                      'w-10 h-10 rounded-full flex items-center justify-center transition-colors',
                      isCompleted
                        ? 'bg-primary text-primary-foreground'
                        : isActive
                        ? 'bg-primary/20 text-primary border-2 border-primary'
                        : 'bg-muted text-muted-foreground'
                    )}
                  >
                    {isCompleted ? (
                      <CheckCircle className="h-5 w-5" />
                    ) : (
                      <Icon className="h-5 w-5" />
                    )}
                  </div>
                  <span
                    className={cn(
                      'text-xs mt-2 hidden sm:block',
                      isActive ? 'text-primary font-medium' : 'text-muted-foreground'
                    )}
                  >
                    {step.label}
                  </span>
                </div>
                {idx < steps.length - 1 && (
                  <div
                    className={cn(
                      'flex-1 h-0.5 mx-2',
                      idx < stepOrder ? 'bg-primary' : 'bg-muted'
                    )}
                  />
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {/* Error display */}
      {error && (
        <Alert variant="destructive" className="mb-6">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Step content */}
      <Card>
        <CardContent className="pt-6">
          {activeStep === 'datasets' && renderDatasetStep()}
          {activeStep === 'search' && renderSearchStep()}
          {activeStep === 'results' && renderResultsStep()}
          {activeStep === 'import' && renderImportStep()}
        </CardContent>
      </Card>
    </div>
  );
};

export default DOLDatasetBrowser;
